import React from 'react';
import './multi-modal-content.scss';
import { MessageContent } from '@langchain/core/messages';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageMetadata } from './threadUtils';
import SpecialMessageArtifacts from './SpecialMessageArtifacts';
import ImageContent from './ImageContent';
import FileContent from './FileContent';

interface MultiModalContentProps {
  content: MessageContent;
  metadata?: MessageMetadata;
  threadId?: string;
  colors?: Record<string, string>;
}

// Reusable component for rendering text content
const TextContent: React.FC<{
  text: string;
  index?: number;
  metadata?: MessageMetadata;
  threadId?: string;
  colors?: Record<string, string>;
}> = ({ text, index, metadata, threadId, colors }) => {
  // Remove any "[Attached: ...]" text
  const cleanedText = text.replace(/\n\n\[Attached:.*?\]/g, '');

  // Only render non-empty text
  if (cleanedText.trim() === '') {
    return null;
  }

  return (
    <div
      key={index !== undefined ? `text-${index}` : undefined}
      className='text-content'
      style={colors}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Prevent h2 headings from being rendered - convert to regular paragraphs
          h2: ({ children }) => <p>{children}</p>,
          // You can also prevent other heading levels if needed
          h1: ({ children }) => <p>{children}</p>,
          h3: ({ children }) => <p>{children}</p>,
          h4: ({ children }) => <p>{children}</p>,
          h5: ({ children }) => <p>{children}</p>,
          h6: ({ children }) => <p>{children}</p>,
        }}
      >
        {cleanedText}
      </ReactMarkdown>
      {metadata?.specialMessageArtifacts && threadId && (
        <SpecialMessageArtifacts
          specialMessageArtifacts={metadata.specialMessageArtifacts}
          threadId={threadId}
        />
      )}
    </div>
  );
};

const MultiModalContent: React.FC<MultiModalContentProps> = ({
  content,
  metadata,
  threadId,
  colors,
}) => {
  // First collect all items by type
  const textItems: React.ReactNode[] = [];
  const mediaItems: React.ReactNode[] = [];

  // Check for media items in specialMessageArtifacts first
  if (metadata?.specialMessageArtifacts) {
    metadata.specialMessageArtifacts.forEach((artifact, artifactIndex) => {
      if (artifact.type === 'imagesToView' && artifact.data.images) {
        artifact.data.images.forEach((image, imageIndex) => {
          mediaItems.push(
            <ImageContent
              key={`artifact-image-${artifactIndex}-${imageIndex}`}
              imageUrl={image.url}
              alt={image.caption || image.filename || 'Message attachment'}
            />,
          );
        });
      } else if (artifact.type === 'filesToView' && artifact.data.files) {
        artifact.data.files.forEach((file, fileIndex) => {
          mediaItems.push(
            <FileContent
              key={`artifact-file-${artifactIndex}-${fileIndex}`}
              fileUrl={file.url}
              filename={file.filename}
              mimeType={file.mimeType}
            />,
          );
        });
      }
    });
  }

  // Handle content - if string, convert to text item; if array, process each item
  if (typeof content === 'string') {
    // Add string content as a text item
    const textContent = (
      <TextContent
        text={content}
        metadata={metadata}
        threadId={threadId}
        colors={colors}
      />
    );
    if (textContent) {
      textItems.push(textContent);
    }
  } else {
    // Process array content
    content.forEach((item, index) => {
      // Handle text content - collect but don't render yet
      if (item.type === 'text') {
        const textContent = (
          <TextContent
            text={item.text}
            index={index}
            metadata={metadata}
            threadId={threadId}
            colors={colors}
          />
        );
        if (textContent) {
          textItems.push(textContent);
        }
      }
      // Handle image content
      else if (item.type === 'image_url') {
        const imageUrl =
          typeof item.image_url === 'string'
            ? item.image_url
            : item.image_url.url;

        mediaItems.push(
          <ImageContent
            key={`image-${index}`}
            imageUrl={imageUrl}
            alt='Message attachment'
          />,
        );
      }
      // Handle file content
      else if (item.type === 'file') {
        // Only render file if we have a URL
        if (item.url) {
          mediaItems.push(
            <FileContent
              key={`file-${index}`}
              fileUrl={item.url}
              filename={item.metadata?.filename}
              mimeType={item.mime_type}
            />,
          );
        }
      }
    });
  }
  // Render with media items first, then text items
  return (
    <div className='multi-modal-wrapper'>
      {mediaItems}
      {textItems}
    </div>
  );
};

export default MultiModalContent;
